import api from './api';

export interface OnboardingStatus {
    pendente: boolean;
    dores: string[];
    placasRestantes: number;
    limitePlacas: number;
    // Campos novos do Status: podem faltar enquanto o servidor não for atualizado.
    idLocacao?: string | null;
    idModeloContrato?: string | null;
    veiculosLivres?: number;
    totalVeiculos?: number;
}

export interface VeiculoPrevia {
    placa: string;
    encontrado: boolean;
    jaCadastrado: boolean;
    foraDoLimite: boolean;
    tipoVeiculo: number;
    marca: string;
    modelo: string;
    ano: string;
    cor?: string;
    chassi?: string;
    valorFipe?: number;
    mensagem?: string;
}

export interface PlacasResultado {
    veiculos: VeiculoPrevia[];
    placasRestantes: number;
    limitePlacas: number;
}

export interface CadastroVeiculosResultado {
    cadastrados: number;
    erros: string[];
}

export interface AcessoLocatario {
    nomeLocatario: string;
    login: string;
    /** Vem vazia quando o cliente já entrou no app: a senha dele não é trocada. */
    senha: string | null;
    jaUsaOApp?: boolean;
}

export interface MarcacaoContrato {
    paragrafo: number;
    inicio?: number;
    trecho: string;
    variavel: string;
    descricao?: string;
    antes?: string;
    depois?: string;
}

export interface AnaliseContrato {
    marcacoes: MarcacaoContrato[];
    variaveis: Record<string, string>;
    arquivoConvertido?: string;
    paragrafos: { indice: number; texto: string }[];
    /** A leitura falhou do nosso lado (não é culpa do arquivo); marcacoes vem vazia. */
    falhaNaLeitura?: boolean;
    /** O PDF foi convertido em um Word simples. */
    convertidoDePdf?: boolean;
    avisos?: string[];
}

export interface ContratoSalvo {
    idModelo: string;
    avisos: string[];
}

/** Variável do contrato onde vai o nome do cliente; sem ela o contrato não serve de modelo. */
export const VARIAVEL_NOME_LOCATARIO = 'nome_locatario';

export const TAMANHO_MAXIMO_ARQUIVO = 10 * 1024 * 1024;

const formularioContrato = (arquivo: File, marcacoes?: MarcacaoContrato[], titulo?: string) => {
    const dados = new FormData();
    dados.append('arquivo', arquivo);
    if (marcacoes) dados.append('marcacoes', JSON.stringify(marcacoes));
    if (titulo) dados.append('titulo', titulo);
    return dados;
};

export const onboardingService = {
    status: () => api.get<OnboardingStatus>('/Onboarding/Status').then(r => r.data),
    salvarDores: (dores: string[]) => api.post('/Onboarding/Dores', { dores }),
    interpretarPlacas: (texto: string) => api.post<PlacasResultado>('/Onboarding/InterpretarPlacas', { texto }).then(r => r.data),
    cadastrarVeiculos: (veiculos: VeiculoPrevia[]) => api.post<CadastroVeiculosResultado>('/Onboarding/Veiculos', { veiculos }).then(r => r.data),
    gerarAcessoLocatario: (idLocacao: string) => api.post<AcessoLocatario>(`/Onboarding/AcessoLocatario/${idLocacao}`).then(r => r.data),
    appLocadorConectado: () => api.get<{ conectado: boolean }>('/Onboarding/AppLocador').then(r => r.data.conectado),
    concluir: () => api.post('/Onboarding/Concluir'),
    analisarContrato: (arquivo: File, signal?: AbortSignal) => analisarContratoEm('/Onboarding/Contrato', arquivo, signal),
    salvarContrato: (arquivo: File, marcacoes: MarcacaoContrato[], titulo: string) => salvarContratoEm('/Onboarding/Contrato', arquivo, marcacoes, titulo),
};

const analisarContratoEm = (rota: string, arquivo: File, signal?: AbortSignal) =>
    api.post<AnaliseContrato>(`${rota}/Analisar`, formularioContrato(arquivo), { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 240000, signal }).then(r => r.data);

const salvarContratoEm = (rota: string, arquivo: File, marcacoes: MarcacaoContrato[], titulo: string) =>
    api.post<ContratoSalvo>(`${rota}/Salvar`, formularioContrato(arquivo, marcacoes, titulo), { headers: { 'Content-Type': 'multipart/form-data' } })
        .then(r => ({ idModelo: r.data?.idModelo, avisos: Array.isArray(r.data?.avisos) ? r.data.avisos : [] }));

/** A mesma leitura do onboarding, na tela de Modelos de Contrato (permissão de cadastrar modelo). */
export const aprenderContratoService = {
    analisar: (arquivo: File, signal?: AbortSignal) => analisarContratoEm('/ModeloContrato/Aprender', arquivo, signal),
    salvar: (arquivo: File, marcacoes: MarcacaoContrato[], titulo: string) => salvarContratoEm('/ModeloContrato/Aprender', arquivo, marcacoes, titulo),
};

// Mensagem que não foi escrita para o usuário: exceção do servidor, texto em inglês, código de erro.
const SINAIS_TECNICOS = /exception|object reference|hexadecimal|status code|network error|timeout of|stack ?trace|npgsql|could not|violates|duplicate key|instance of|\bnull\b|\bundefined\b|\b\d{5}:/i;
const PALAVRAS_EM_INGLES = /\b(the|not|of|is|was|failed|error|invalid|cannot|unable|request|value|must)\b/i;
const ACENTOS = /[ãõçáéíóúâêôà]/i;

export const pareceMensagemTecnica = (mensagem?: string): boolean => {
    const texto = (mensagem ?? '').trim();
    if (!texto) return true;
    if (SINAIS_TECNICOS.test(texto)) return true;
    return PALAVRAS_EM_INGLES.test(texto) && !ACENTOS.test(texto);
};

/**
 * Texto de erro para mostrar ao usuário. Usa a mensagem do servidor só quando ela é dele para o
 * usuário (pt-BR, sem cara de exceção); falha de rede, tempo esgotado e erro 5xx viram o texto padrão.
 */
export const mensagemDeErro = (error: any, padrao: string): string => {
    const resposta = error?.response;
    if (!resposta) return padrao;
    if (resposta.status === 413) return 'O arquivo é grande demais. Envie um arquivo de até 10 MB.';
    if (resposta.status >= 500) return padrao;
    const dados = resposta.data;
    const mensagem = typeof dados === 'string' ? dados : (dados?.mensagem || dados?.Mensagem);
    return mensagem && !pareceMensagemTecnica(mensagem) ? mensagem : padrao;
};
